import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useMutation } from '@apollo/client';
import { Eye, EyeOff, Mail, Lock, User, AlertCircle, ArrowLeft } from 'lucide-react';

import { REGISTER_MUTATION } from '../services/graphql/mutations.js';
import { loginStart, loginSuccess, loginFailure } from '../store/slices/authSlice.js';
import { addAlert } from '../store/slices/uiSlice.js';

const Register = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  const { loading } = useSelector((state) => state.auth);
  
  // Form state according to Login.md: email, full_name, password and password re-type
  const [formData, setFormData] = useState({
    email: '',
    full_name: '',
    password: '',
    confirmPassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});

  // GraphQL mutation
  const [registerMutation] = useMutation(REGISTER_MUTATION);

  // Validation according to Login.md requirements
  const validateForm = () => {
    const newErrors = {};
    
    // Email validation
    if (!formData.email) {
      newErrors.email = 'El correo electrónico es requerido';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Formato de correo electrónico inválido';
    }
    
    // Full name validation
    if (!formData.full_name || formData.full_name.trim().length === 0) {
      newErrors.full_name = 'El nombre completo es requerido';
    }
    
    // Password validation - length >=6 as per Login.md
    if (!formData.password) {
      newErrors.password = 'La contraseña es requerida';
    } else if (formData.password.length < 6) {
      newErrors.password = 'La contraseña debe tener al menos 6 caracteres';
    }
    
    // Confirm password validation - must match password as per Login.md
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Confirme su contraseña';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Las contraseñas no coinciden';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    dispatch(loginStart());

    try {
      const { data, errors } = await registerMutation({
        variables: {
          input: {
            email: formData.email.toLowerCase().trim(),
            full_name: formData.full_name.trim(),
            password: formData.password,
          },
        },
        errorPolicy: 'all',
      });

      // Check if we have GraphQL errors but no data
      if (errors && errors.length > 0) {
        throw new Error(errors[0].message);
      }

      // Check if data is null or doesn't have register
      if (!data || !data.register) {
        throw new Error('Error al registrar usuario. Intente nuevamente.');
      }

      const { message } = data.register;
      
      // Clear loading state after successful registration
      dispatch(loginFailure(''));
      
      // Show success message as per Login.md: "Usuario registrado con éxito"
      dispatch(addAlert({
        type: 'success',
        title: 'Registro Exitoso',
        message: message || 'Usuario registrado con éxito',
      }));

      // Return to login page as per Login.md requirement
      setTimeout(() => {
        navigate('/login', { replace: true });
      }, 2000);

    } catch (error) {
      console.error('Registration error:', error);
      
      let errorMessage = 'Error al registrar usuario. Intente nuevamente.';
      
      // Check if it's a GraphQL error
      if (error.graphQLErrors && error.graphQLErrors.length > 0) {
        errorMessage = error.graphQLErrors[0].message;
      } else if (error.networkError) {
        errorMessage = 'Error de conexión. Intente nuevamente.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      // Check for specific error types after initial errorMessage assignment
      if (errorMessage.includes('ya está registrado')) {
        // User already exists message as per Login.md
        errorMessage = 'Usted ya está registrado en el sistema';
      } else if (errorMessage.includes('inactivo')) {
        // Inactive user message as per Login.md
        errorMessage = 'Su usuario está inactivo, favor contactar al administrador para activación de cuenta';
      }
      
      dispatch(loginFailure(errorMessage));
      
      dispatch(addAlert({
        type: 'error',
        title: 'Error de Registro',
        message: errorMessage,
      }));
    }
  };

  const handleCancel = () => {
    // Return to Login page as per Login.md requirement
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-secondary-900 mb-2">
            Registro de Usuario
          </h1>
          <p className="text-secondary-600">
            Complete el formulario para crear su cuenta
          </p>
        </div>

        <div className="card">
          <div className="card-body">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Email field */}
              <div className="form-group">
                <label htmlFor="email" className="form-label">
                  Correo electrónico
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-secondary-400" />
                  </div>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    className={`form-input pl-10 ${errors.email ? 'border-error-500 focus:border-error-500 focus:ring-error-500' : ''}`}
                    placeholder="Digite su correo electrónico"
                    value={formData.email}
                    onChange={handleChange}
                  />
                </div>
                {errors.email && (
                  <div className="flex items-center mt-1 text-error-600">
                    <AlertCircle className="h-4 w-4 mr-1" />
                    <span className="form-error">{errors.email}</span>
                  </div>
                )}
              </div>

              {/* Full name field */}
              <div className="form-group">
                <label htmlFor="full_name" className="form-label">
                  Nombre completo
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <User className="h-5 w-5 text-secondary-400" />
                  </div>
                  <input
                    id="full_name"
                    name="full_name"
                    type="text"
                    autoComplete="name"
                    required
                    className={`form-input pl-10 ${errors.full_name ? 'border-error-500 focus:border-error-500 focus:ring-error-500' : ''}`}
                    placeholder="Digite su nombre completo"
                    value={formData.full_name}
                    onChange={handleChange}
                  />
                </div>
                {errors.full_name && (
                  <div className="flex items-center mt-1 text-error-600">
                    <AlertCircle className="h-4 w-4 mr-1" />
                    <span className="form-error">{errors.full_name}</span>
                  </div>
                )}
              </div>

              {/* Password field */}
              <div className="form-group">
                <label htmlFor="password" className="form-label">
                  Contraseña
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-secondary-400" />
                  </div>
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    required
                    className={`form-input pl-10 pr-10 ${errors.password ? 'border-error-500 focus:border-error-500 focus:ring-error-500' : ''}`}
                    placeholder="Digite su contraseña (mínimo 6 caracteres)"
                    value={formData.password}
                    onChange={handleChange}
                  />
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                    <button
                      type="button"
                      className="text-secondary-400 hover:text-secondary-600 focus:outline-none"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <EyeOff className="h-5 w-5" />
                      ) : (
                        <Eye className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                </div>
                {errors.password && (
                  <div className="flex items-center mt-1 text-error-600">
                    <AlertCircle className="h-4 w-4 mr-1" />
                    <span className="form-error">{errors.password}</span>
                  </div>
                )}
              </div>

              {/* Confirm Password field */}
              <div className="form-group">
                <label htmlFor="confirmPassword" className="form-label">
                  Confirmar contraseña
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-secondary-400" />
                  </div>
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    required
                    className={`form-input pl-10 pr-10 ${errors.confirmPassword ? 'border-error-500 focus:border-error-500 focus:ring-error-500' : ''}`}
                    placeholder="Confirme su contraseña"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                  />
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                    <button
                      type="button"
                      className="text-secondary-400 hover:text-secondary-600 focus:outline-none"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="h-5 w-5" />
                      ) : (
                        <Eye className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                </div>
                {errors.confirmPassword && (
                  <div className="flex items-center mt-1 text-error-600">
                    <AlertCircle className="h-4 w-4 mr-1" />
                    <span className="form-error">{errors.confirmPassword}</span>
                  </div>
                )}
              </div>

              {/* Information note */}
              <div className="bg-secondary-50 border border-secondary-200 rounded-lg p-4">
                <p className="text-sm text-secondary-700">
                  <strong>Nota:</strong> Su cuenta será revisada por un administrador antes de ser activada. 
                  Recibirá una notificación por correo electrónico una vez que su cuenta esté lista para usar.
                </p>
              </div>

              {/* Action buttons */}
              <div className="flex space-x-4">
                {/* Cancel button as per Login.md */}
                <button
                  type="button"
                  onClick={handleCancel}
                  className="btn-outline flex-1 flex items-center justify-center"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Cancelar {/* Button text as per Login.md */}
                </button>

                {/* Submit button as per Login.md */}
                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary flex-1 flex items-center justify-center"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Creando...
                    </>
                  ) : (
                    'Crear cuenta' // Button text as per Login.md
                  )}
                </button>
              </div>

              {/* Login link */}
              <div className="text-center">
                <p className="text-sm text-secondary-600">
                  ¿Ya tiene una cuenta?{' '}
                  <Link
                    to="/login"
                    className="text-primary-600 hover:text-primary-700 font-medium transition-colors duration-200"
                  >
                    Inicie sesión aquí
                  </Link>
                </p>
              </div>
            </form>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center">
          <p className="text-xs text-secondary-500">
            © 2025 ACTrack. Todos los derechos reservados.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;